import { useState, useEffect } from "react"
import { Mail, Phone, MapPin, Facebook, Instagram, Send, CreditCard, CheckCircle, Loader2, RotateCcw, MessageCircle, Headphones, Package, RefreshCw } from "lucide-react"
import { BASE_URL } from "../../apiConfig"
import { useAuth } from "../../context/AuthProvider";
import { toast } from 'sonner'
import { useLanguage } from "../../context/LanguageProvider";

export default function ConfigForm() {
  const { t } = useLanguage();
  const { token, role, roles } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    address: "",
    facebook: "",
    instagram: "",
    telegram: "",
    phone_support: "",
    phone_payment: "",
    phone_stock: "",
    phone_change: "",
  })

  const [isLoading, setIsLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(true)
  const [errors, setErrors] = useState({})
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    fetchConfig()
  }, [])

  const fetchConfig = async () => {
    setIsFetching(true)
    try {
      const response = await fetch(BASE_URL + "/api/config")
      
      if (response.ok) {
        const data = await response.json()
        if (data) {
          setFormData({
            email: data.email || "",
            address: data.address || "",
            facebook: data.facebook || "",
            instagram: data.instagram || "",
            telegram: data.telegram || "",
            phone: data.phone || "",
            phone_support: data.phone_support || "",
            phone_payment: data.phone_payment || "",
            phone_stock: data.phone_stock || "",
            phone_change: data.phone_change || "",
          })
        }
      }
      if(response.status === 403){
        toast.error(t('messages.no_permission'))
      }
      
    } catch (error) {
      console.error("Error fetching config:", error)
      toast.error(t('configs.errors.load_failed'))
    } finally {
      setIsFetching(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" })
    }
  }

  const validateEmail = (email) => {
    return /^[^\s.]+.[^\s.]+\.[^\s.]+$/.test(email)
  }

  const validateUrl = (url) => {
    if (!url) return true // Optional field
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = t('configs.errors.email_invalid')
    }

    if (formData.facebook && !validateUrl(formData.facebook)) {
      newErrors.facebook = t('configs.errors.facebook_invalid')
    }

    if (formData.instagram && !validateUrl(formData.instagram)) {
      newErrors.instagram = t('configs.errors.instagram_invalid')
    }

    if (formData.telegram && !validateUrl(formData.telegram)) {
      newErrors.telegram = t('configs.errors.telegram_invalid')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return
    setIsLoading(true)

    try {
      const response = await fetch(BASE_URL + "/api/config", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData),
      })

      const result = await response.json()
      if (result.success) {
        setSuccess(true)
        toast.success(t('configs.success_updated'))
        setTimeout(() => setSuccess(false), 3000)
      } else {
        toast.error(t('configs.errors.update_failed'))
      }
      if(response.status === 403){
        toast.error(t('messages.no_permission'))
      }
    } catch (error) {
      console.error("Error updating config:", error)
      toast.error(t('configs.errors.generic_error'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    fetchConfig()
    setErrors({})
    toast.info(t('configs.form_reset'))
  }

  if (isFetching) {
    return (
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--color-primary-500)]" />
        <span className="ml-3 text-gray-600">{t('configs.loading')}</span>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
      {/* Success Message */}
      {success && (
        <div className="bg-[var(--color-primary-500)] text-white p-6 text-center">
          <div className="flex items-center justify-center mb-2">
            <CheckCircle className="h-8 w-8 mr-3" />
            <span className="text-lg font-semibold">{t('configs.success_title')}</span>
          </div>
          <p className="text-[var(--color-primary-50)] text-xs">{t('configs.success_description')}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-8">
        {/* Contact Information */}
        <div className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-6">{t('configs.sections.contact_info')}</h2>

          <div className="grid grid-cols-1 gap-6">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block font-semibold text-sm text-gray-900 mb-3">
                {t('configs.labels.email')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t('configs.placeholders.email')}
                  className={`w-full pl-12 py-3 px-4 text-xs rounded-2xl border-2 outline-none transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.email
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                />
              </div>
              {errors.email && <p className="mt-2 text-red-600 text-xs">{errors.email}</p>}
            </div>

            {/* Phone */}
            {/* <div>
              <label htmlFor="phone" className="block font-semibold text-sm text-gray-900 mb-3">
                Téléphone principal
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1 (555) 123-4567"
                  className={`w-full pl-12 py-3 px-4 text-xs rounded-2xl border-2 outline-none transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.phone
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                />
              </div>
              {errors.phone && <p className="mt-2 text-red-600 text-xs">{errors.phone}</p>}
            </div> */}
          </div>

          {/* Address */}
          <div className="mt-6">
            <label htmlFor="address" className="block font-semibold text-sm text-gray-900 mb-3">
              {t('configs.labels.address')}
            </label>
            <div className="relative">
              <div className="absolute top-3 left-0 pl-4 flex items-start pointer-events-none">
                <MapPin className="h-5 w-5 text-gray-400" />
              </div>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder={t('configs.placeholders.address')}
                rows={3}
                className={`w-full pl-12 py-3 px-4 text-xs rounded-2xl border-2 outline-none transition-all duration-200 bg-gray-50 focus:bg-white ${
                  errors.address
                    ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                    : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                }`}
              />
            </div>
            {errors.address && <p className="mt-2 text-red-600 text-xs">{errors.address}</p>}
          </div>
        </div>

        {/* Phone Numbers Section */}
        <div className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-6">{t('configs.sections.phone_numbers')}</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Support Phone */}
            <div>
              <label htmlFor="phone_support" className="block font-semibold text-sm text-gray-900 mb-3">
                {t('configs.labels.support_phone')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Headphones className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="tel"
                  id="phone_support"
                  name="phone_support"
                  value={formData.phone_support}
                  onChange={handleInputChange}
                  placeholder="+212 123-4567"
                  className="w-full pl-12 py-3 px-4 text-xs rounded-2xl border-2 outline-none transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                />
              </div>
            </div>

            {/* Payment Phone */}
            <div>
              <label htmlFor="phone_payment" className="block font-semibold text-sm text-gray-900 mb-3">
                {t('configs.labels.payment_phone')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <CreditCard className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="tel"
                  id="phone_payment"
                  name="phone_payment"
                  value={formData.phone_payment}
                  onChange={handleInputChange}
                  placeholder="+212 123-4567"
                  className="w-full pl-12 py-3 px-4 text-xs rounded-2xl border-2 outline-none transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                />
              </div>
            </div>

            {/* Stock Phone */}
            <div>
              <label htmlFor="phone_stock" className="block font-semibold text-sm text-gray-900 mb-3">
                {t('configs.labels.stock_phone')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Package className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="tel"
                  id="phone_stock"
                  name="phone_stock"
                  value={formData.phone_stock}
                  onChange={handleInputChange}
                  placeholder="+212 123-4567"
                  className="w-full pl-12 py-3 px-4 text-xs rounded-2xl border-2 outline-none transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                />
              </div>
            </div>

            {/* Change Phone */}
            <div>
              <label htmlFor="phone_change" className="block font-semibold text-sm text-gray-900 mb-3">
                {t('configs.labels.exchange_phone')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <RefreshCw className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="tel"
                  id="phone_change"
                  name="phone_change"
                  value={formData.phone_change}
                  onChange={handleInputChange}
                  placeholder="+212 123-4567"
                  className="w-full pl-12 py-3 px-4 text-xs rounded-2xl border-2 outline-none transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-6">{t('configs.sections.social_media')}</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Facebook */}
            <div>
              <label htmlFor="facebook" className="block font-semibold text-sm text-gray-900 mb-3">
                {t('configs.labels.facebook_url')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Facebook className="h-5 w-5 text-blue-600" />
                </div>
                <input
                  type="url"
                  id="facebook"
                  name="facebook"
                  value={formData.facebook}
                  onChange={handleInputChange}
                  placeholder={t('configs.placeholders.facebook')}
                  className={`w-full pl-12 py-3 px-4 text-xs rounded-2xl outline-none border-2 transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.facebook
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                />
              </div>
              {errors.facebook && <p className="mt-2 text-red-600 text-xs">{errors.facebook}</p>}
            </div>

            {/* Instagram */}
            <div>
              <label htmlFor="instagram" className="block text-sm font-semibold text-gray-900 mb-3">
                {t('configs.labels.instagram_url')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Instagram className="h-5 w-5 text-pink-600" />
                </div>
                <input
                  type="url"
                  id="instagram"
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleInputChange}
                  placeholder={t('configs.placeholders.instagram')}
                  className={`w-full pl-12 py-3 px-4 text-xs rounded-2xl outline-none border-2 transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.instagram
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                />
              </div>
              {errors.instagram && <p className="mt-2 text-red-600 text-xs">{errors.instagram}</p>}
            </div>

            {/* Telegram */}
            <div>
              <label htmlFor="telegram" className="block text-sm font-semibold text-gray-900 mb-3">
                {t('configs.labels.telegram_url')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg width="20px" height="20px" viewBox="0 0 24 24"  xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" fill="#0088CC" d="M23.1117 4.49449C23.4296 2.94472 21.9074 1.65683 20.4317 2.227L2.3425 9.21601C0.694517 9.85273 0.621087 12.1572 2.22518 12.8975L6.1645 14.7157L8.03849 21.2746C8.13583 21.6153 8.40618 21.8791 8.74917 21.968C9.09216 22.0568 9.45658 21.9576 9.70712 21.707L12.5938 18.8203L16.6375 21.8531C17.8113 22.7334 19.5019 22.0922 19.7967 20.6549L23.1117 4.49449ZM3.0633 11.0816L21.1525 4.0926L17.8375 20.2531L13.1 16.6999C12.7019 16.4013 12.1448 16.4409 11.7929 16.7928L10.5565 18.0292L10.928 15.9861L18.2071 8.70703C18.5614 8.35278 18.5988 7.79106 18.2947 7.39293C17.9906 6.99479 17.4389 6.88312 17.0039 7.13168L6.95124 12.876L3.0633 11.0816ZM8.17695 14.4791L8.78333 16.6015L9.01614 15.321C9.05253 15.1209 9.14908 14.9366 9.29291 14.7928L11.5128 12.573L8.17695 14.4791Z" />
                  </svg>
                </div>
                <input
                  type="url"
                  id="telegram"
                  name="telegram"
                  value={formData.telegram}
                  onChange={handleInputChange}
                  placeholder={t('configs.placeholders.telegram')}
                  className={`w-full pl-12 py-3 px-4 text-xs rounded-2xl outline-none border-2 transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.telegram
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                />
              </div>
              {errors.telegram && <p className="mt-2 text-red-600 text-xs">{errors.telegram}</p>}
            </div>
          </div>
        </div>

        {/* Form Actions */}
        {
          (role === 1 || (role === 2 && roles.edit_config === 1)) && (
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleReset}
                className="flex-1 cursor-pointer py-3 px-4 text-sm rounded-2xl border-2 border-gray-200 text-gray-700 font-semibold hover:border-gray-300 hover:bg-gray-50 transition-all duration-200 flex items-center justify-center"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                {t('buttons.reset')}
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className={`flex-1 cursor-pointer font-semibold py-3 px-4 text-sm rounded-2xl transition-all duration-200 flex items-center justify-center ${
                  isLoading
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : "bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white shadow-lg hover:shadow-xl transform hover:scale-105"
                }`}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin h-5 w-5 mr-2" />
                    {t('buttons.saving')}
                  </>
                ) : (
                  <>
                    {t('buttons.save')}
                    <CheckCircle className="h-5 w-5 ml-2" />
                  </>
                )}
              </button>
            </div>
          )
        }

        {/* Preview */}
        <div className="mt-8 p-6 bg-gray-50 rounded-2xl">
          <h3 className="font-semibold text-gray-900 mb-4">{t('configs.preview.title')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-sm text-gray-900 mb-3">{t('configs.preview.contact_info')}</h4>
              <div className="space-y-2 text-xs text-gray-600">
                {formData.email && (
                  <div className="flex items-center">
                    <Mail className="h-4 w-4 mr-2 text-gray-400" />
                    {formData.email}
                  </div>
                )}
                {formData.phone && (
                  <div className="flex items-center">
                    <Phone className="h-4 w-4 mr-2 text-gray-400" />
                    {formData.phone}
                  </div>
                )}
                {formData.address && (
                  <div className="flex items-start">
                    <MapPin className="h-4 w-4 mr-2 mt-0.5 text-gray-400" />
                    {formData.address}
                  </div>
                )}
              </div>
              
              {(formData.phone_support || formData.phone_payment || formData.phone_stock || formData.phone_change) && (
                <>
                  <h4 className="font-medium text-sm text-gray-900 mb-3 mt-4">{t('configs.preview.specialized_phones')}</h4>
                  <div className="space-y-2 text-xs text-gray-600">
                    {formData.phone_support && (
                      <div className="flex items-center">
                        <Headphones className="h-4 w-4 mr-2 text-gray-400" />
                        {t('configs.preview.support')}: {formData.phone_support}
                      </div>
                    )}
                    {formData.phone_payment && (
                      <div className="flex items-center">
                        <Send className="h-4 w-4 mr-2 text-gray-400" />
                        {t('configs.preview.payment')}: {formData.phone_payment}
                      </div>
                    )}
                    {formData.phone_stock && (
                      <div className="flex items-center">
                        <Package className="h-4 w-4 mr-2 text-gray-400" />
                        {t('configs.preview.stock')}: {formData.phone_stock}
                      </div>
                    )}
                    {formData.phone_change && (
                      <div className="flex items-center">
                        <RefreshCw className="h-4 w-4 mr-2 text-gray-400" />
                        {t('configs.preview.exchange')}: {formData.phone_change}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div>
              <h4 className="font-medium text-sm text-gray-900 mb-3">{t('configs.preview.social_media')}</h4>
              <div className="space-y-2 text-xs text-gray-600">
                {formData.facebook && (
                  <div className="flex items-center">
                    <Facebook className="h-4 w-4 mr-2 text-blue-600" />
                    Facebook
                  </div>
                )}
                {formData.instagram && (
                  <div className="flex items-center">
                    <Instagram className="h-4 w-4 mr-2 text-pink-600" />
                    Instagram
                  </div>
                )}
                {formData.telegram && (
                  <div className="flex items-center">
                    <MessageCircle className="h-4 w-4 mr-2 text-blue-500" />
                    Telegram
                  </div>
                )}
                {!formData.facebook && !formData.instagram && !formData.telegram && (
                  <p className="text-gray-400 italic">{t('configs.preview.no_social')}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}